
import { useState } from 'react';

interface CertFile {
  id: number;
  name: string;
  type: string;
  expireDate: string;
  uploadDate: string;
  status: 'valid' | 'expiring' | 'expired';
  fileUrl: string;
}

const initialCerts: CertFile[] = [
  {
    id: 1,
    name: '营业执照',
    type: '营业执照',
    expireDate: '2027-03-15',
    uploadDate: '2024-03-15',
    status: 'valid',
    fileUrl: '',
  },
  {
    id: 2,
    name: '粮食收购许可证',
    type: '行政许可证',
    expireDate: '2026-03-20',
    uploadDate: '2024-03-20',
    status: 'expiring',
    fileUrl: '',
  },
  {
    id: 3,
    name: '食品经营许可证',
    type: '行政许可证',
    expireDate: '2025-12-31',
    uploadDate: '2023-12-31',
    status: 'expired',
    fileUrl: '',
  },
  {
    id: 4,
    name: '税务登记证',
    type: '税务证件',
    expireDate: '2028-06-30',
    uploadDate: '2024-06-30',
    status: 'valid',
    fileUrl: '',
  },
];

const getDaysUntilExpiry = (dateStr: string) => {
  const today = new Date();
  const expiry = new Date(dateStr);
  return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

export default function CompanyTab({ showSuccess }: { showSuccess: (msg: string) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const [certs, setCerts] = useState<CertFile[]>(initialCerts);
  const [showAddCert, setShowAddCert] = useState(false);
  const [newCert, setNewCert] = useState({ name: '', type: '营业执照', expireDate: '' });
  const [companyInfo, setCompanyInfo] = useState({
    name: '河南省中原粮食贸易有限公司',
    creditCode: '91410100MA4XXXXXXX',
    legalPerson: '张建国',
    registeredCapital: '5000万元',
    establishDate: '2018-03-15',
    businessScope: '粮食收购、储存、销售；农产品批发；粮食加工',
    address: '河南省郑州市金水区中原路188号粮食大厦12层',
    phone: '0371-88888888',
    email: 'contact@grain.com',
    website: 'www.grain.com',
    postalCode: '450000',
    fax: '0371-88888889',
  });

  const expiredCount = certs.filter((c) => c.status === 'expired').length;
  const expiringCount = certs.filter((c) => c.status === 'expiring').length;

  const handleAddCert = () => {
    if (!newCert.name || !newCert.expireDate) return;
    const days = getDaysUntilExpiry(newCert.expireDate);
    const status: CertFile['status'] = days < 0 ? 'expired' : days <= 90 ? 'expiring' : 'valid';
    setCerts([
      ...certs,
      {
        id: Date.now(),
        name: newCert.name,
        type: newCert.type,
        expireDate: newCert.expireDate,
        uploadDate: new Date().toISOString().split('T')[0],
        status,
        fileUrl: '',
      },
    ]);
    setNewCert({ name: '', type: '营业执照', expireDate: '' });
    setShowAddCert(false);
    showSuccess('证件添加成功');
  };

  const handleDeleteCert = (id: number) => {
    setCerts(certs.filter((c) => c.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* 证件到期提醒横幅 */}
      {(expiredCount > 0 || expiringCount > 0) && (
        <div className={`rounded-xl p-4 flex items-start space-x-3 ${expiredCount > 0 ? 'bg-rose-50 border border-rose-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${expiredCount > 0 ? 'bg-rose-100' : 'bg-amber-100'}`}>
            <i className={`ri-alarm-warning-line text-lg ${expiredCount > 0 ? 'text-rose-600' : 'text-amber-600'}`}></i>
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${expiredCount > 0 ? 'text-rose-700' : 'text-amber-700'}`}>
              证件到期提醒
            </p>
            <p className={`text-xs mt-0.5 ${expiredCount > 0 ? 'text-rose-600' : 'text-amber-600'}`}>
              {expiredCount > 0 && `${expiredCount} 份证件已过期，`}
              {expiringCount > 0 && `${expiringCount} 份证件即将在90天内到期，`}
              请及时更新相关证件以确保合规经营。
            </p>
          </div>
        </div>
      )}

      {/* 企业基本信息 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">企业基本信息</h2>
            <p className="text-sm text-gray-500 mt-1">管理企业的基础资料和联系方式</p>
          </div>
          <button
            onClick={() => {
              if (isEditing) showSuccess('企业信息保存成功');
              setIsEditing(!isEditing);
            }}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
              isEditing ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <i className={`${isEditing ? 'ri-save-line' : 'ri-edit-line'} mr-2`}></i>
            {isEditing ? '保存信息' : '编辑信息'}
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-5">
            {[
              { label: '企业名称', key: 'name', span: 2, required: true },
              { label: '统一社会信用代码', key: 'creditCode', required: true },
              { label: '法定代表人', key: 'legalPerson', required: true },
              { label: '注册资本', key: 'registeredCapital' },
              { label: '成立日期', key: 'establishDate', type: 'date' },
              { label: '联系电话', key: 'phone' },
              { label: '联系邮箱', key: 'email' },
              { label: '企业网站', key: 'website' },
              { label: '邮政编码', key: 'postalCode' },
              { label: '传真号码', key: 'fax' },
              { label: '注册地址', key: 'address', span: 2 },
              { label: '经营范围', key: 'businessScope', span: 2, textarea: true },
            ].map((field) => (
              <div key={field.key} className={field.span === 2 ? 'col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {field.label}
                  {field.required && <span className="text-rose-500 ml-1">*</span>}
                </label>
                {field.textarea ? (
                  <textarea
                    rows={2}
                    value={companyInfo[field.key as keyof typeof companyInfo]}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, [field.key]: e.target.value })}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm resize-none transition-all ${
                      isEditing ? 'focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white' : 'bg-gray-50 cursor-not-allowed'
                    }`}
                  />
                ) : (
                  <input
                    type={field.type || 'text'}
                    value={companyInfo[field.key as keyof typeof companyInfo]}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, [field.key]: e.target.value })}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm transition-all ${
                      isEditing ? 'focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white' : 'bg-gray-50 cursor-not-allowed'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 证件附件管理 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">证件附件管理</h2>
            <p className="text-sm text-gray-500 mt-1">上传并管理企业相关证件，系统自动提醒到期</p>
          </div>
          <button
            onClick={() => setShowAddCert(true)}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>添加证件
          </button>
        </div>

        {showAddCert && (
          <div className="mx-6 mt-5 p-4 bg-teal-50 border border-teal-200 rounded-xl">
            <h4 className="text-sm font-semibold text-teal-700 mb-3">新增证件</h4>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">证件名称 *</label>
                <input
                  type="text"
                  value={newCert.name}
                  onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                  placeholder="如：营业执照"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">证件类型</label>
                <select
                  value={newCert.type}
                  onChange={(e) => setNewCert({ ...newCert, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option>营业执照</option>
                  <option>行政许可证</option>
                  <option>税务证件</option>
                  <option>资质证书</option>
                  <option>其他</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">到期日期 *</label>
                <input
                  type="date"
                  value={newCert.expireDate}
                  onChange={(e) => setNewCert({ ...newCert, expireDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">上传附件</label>
              <div className="border-2 border-dashed border-teal-300 rounded-lg p-4 text-center cursor-pointer hover:bg-teal-50 transition-colors">
                <i className="ri-upload-cloud-2-line text-2xl text-teal-400 mb-1"></i>
                <p className="text-xs text-gray-500">点击或拖拽上传证件扫描件</p>
                <p className="text-xs text-gray-400 mt-0.5">支持 PDF、JPG、PNG，最大 10MB</p>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-2">
              <button onClick={() => setShowAddCert(false)} className="px-4 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleAddCert} className="px-4 py-1.5 bg-teal-600 text-white rounded-lg text-xs hover:bg-teal-700 cursor-pointer whitespace-nowrap">确认添加</button>
            </div>
          </div>
        )}

        <div className="p-6">
          <div className="space-y-3">
            {certs.map((cert) => {
              const days = getDaysUntilExpiry(cert.expireDate);
              return (
                <div key={cert.id} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                  cert.status === 'expired' ? 'border-rose-200 bg-rose-50/40' :
                  cert.status === 'expiring' ? 'border-amber-200 bg-amber-50/40' :
                  'border-gray-200 hover:border-teal-200'
                }`}>
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      cert.status === 'expired' ? 'bg-rose-100' :
                      cert.status === 'expiring' ? 'bg-amber-100' : 'bg-teal-100'
                    }`}>
                      <i className={`ri-file-text-line text-lg ${
                        cert.status === 'expired' ? 'text-rose-600' :
                        cert.status === 'expiring' ? 'text-amber-600' : 'text-teal-600'
                      }`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-800">{cert.name}</p>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">{cert.type}</span>
                        {cert.status === 'expired' && (
                          <span className="px-2 py-0.5 bg-rose-100 text-rose-600 rounded-full text-xs font-medium">已过期</span>
                        )}
                        {cert.status === 'expiring' && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full text-xs font-medium">即将到期</span>
                        )}
                        {cert.status === 'valid' && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded-full text-xs font-medium">有效</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 mt-1">
                        <p className="text-xs text-gray-500">
                          到期日期：<span className={`font-medium ${cert.status === 'expired' ? 'text-rose-600' : cert.status === 'expiring' ? 'text-amber-600' : 'text-gray-700'}`}>{cert.expireDate}</span>
                        </p>
                        <p className="text-xs text-gray-400">上传于 {cert.uploadDate}</p>
                        {cert.status === 'expired' && <p className="text-xs text-rose-500 font-medium">已过期 {Math.abs(days)} 天</p>}
                        {cert.status === 'expiring' && <p className="text-xs text-amber-600 font-medium">还剩 {days} 天到期</p>}
                        {cert.status === 'valid' && days <= 365 && <p className="text-xs text-gray-400">还剩 {days} 天</p>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer">
                      <i className="ri-upload-2-line mr-1"></i>重新上传
                    </button>
                    <button className="px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer">
                      <i className="ri-download-line mr-1"></i>下载
                    </button>
                    <button
                      onClick={() => handleDeleteCert(cert.id)}
                      className="w-7 h-7 flex items-center justify-center text-rose-400 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
